import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { PDFDownloadLink, PDFViewer } from '@react-pdf/renderer';
import { X, FileText, Download, Eye, EyeOff, ChevronDown, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../lib/apiConfig';
import citiesWithCoordinates from '../../data/cities_with_coordinates.json';
import CityReportDocument, { CityReportData, ReportMultiplier, ReportDemand } from './CityReportDocument';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CityOption {
  id: number;       // IBGE / DB city id
  nome: string;
  uf: string;
  population: number;
}

// ─── Styled Components ────────────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity: 0; } to { opacity: 1; }`;
const slideUp = keyframes`from { transform: translateY(16px); opacity: 0; } to { transform: translateY(0); opacity: 1; }`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.2s ease;

  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const slideUpMobile = keyframes`from { transform: translateY(100%); } to { transform: translateY(0); }`;

const Modal = styled.div`
  background: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 700px;
  max-height: 92vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  animation: ${slideUp} 0.25s ease;

  @media (max-width: 768px) {
    max-width: 100%;
    max-height: 92vh;
    border-radius: 20px 20px 0 0;
    animation: ${slideUpMobile} 0.28s cubic-bezier(0.32, 0.72, 0, 1);
  }
`;

const DragHandle = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    width: 36px;
    height: 4px;
    background: #cbd5e1;
    border-radius: 2px;
    margin: 10px auto 0;
    flex-shrink: 0;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px 18px;
  border-bottom: 1px solid #e2e8f0;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 14px 16px 14px;
  }
`;

const ModalIcon = styled.div`
  width: 40px;
  height: 40px;
  background: #eff6ff;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  svg { color: #2563eb; }
  flex-shrink: 0;

  @media (max-width: 768px) {
    display: none;
  }
`;

const ModalTitleBlock = styled.div`
  flex: 1;
`;

const ModalTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 2px 0;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const ModalSubtitle = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: 0;
`;

const CloseBtn = styled.button`
  padding: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  border-radius: 8px;
  color: #94a3b8;
  display: flex;
  align-items: center;
  transition: all 0.15s;
  &:hover { background: #f1f5f9; color: #475569; }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;

  @media (max-width: 768px) {
    padding: 16px 16px;
  }
`;

const ModalFooter = styled.div`
  padding: 14px 24px;
  border-top: 1px solid #e2e8f0;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    flex-direction: column-reverse;
    align-items: stretch;
    gap: 8px;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 14px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 14px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const Input = styled.input`
  padding: 10px 14px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  font-size: 14px;
  color: #1e293b;
  background: #fff;
  transition: all 0.15s;
  width: 100%;
  box-sizing: border-box;
  &:focus {
    outline: none;
    border-color: #2563eb;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
  &::placeholder { color: #94a3b8; }

  @media (max-width: 768px) {
    font-size: 16px; /* prevents iOS zoom */
    padding: 12px 14px;
  }
`;

// ── City Dropdown ──
const CityDropdownWrapper = styled.div`
  position: relative;
`;

const CityDropdownButton = styled.button<{ $hasValue: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1.5px solid ${p => p.$hasValue ? '#2563eb' : '#e2e8f0'};
  border-radius: 10px;
  background: ${p => p.$hasValue ? '#eff6ff' : '#fff'};
  font-size: 14px;
  color: ${p => p.$hasValue ? '#1e40af' : '#94a3b8'};
  font-weight: ${p => p.$hasValue ? '600' : '400'};
  cursor: pointer;
  text-align: left;
  transition: all 0.15s;
  &:focus { outline: none; border-color: #2563eb; box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1); }
  &:hover:not(:disabled) { border-color: #93c5fd; }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 14px;
  }
`;

const CityDropdownMenu = styled.div<{ $top: number; $left: number; $width: number }>`
  position: fixed;
  top: ${p => p.$top}px;
  left: ${p => p.$left}px;
  width: ${p => p.$width}px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  overflow: hidden;

  @media (max-width: 768px) {
    left: 16px;
    right: 16px;
    width: calc(100vw - 32px);
  }
`;

const CitySearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-bottom: 1px solid #f1f5f9;
  svg { color: #94a3b8; width: 14px; height: 14px; flex-shrink: 0; }
  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 13px;
    color: #1e293b;
    &::placeholder { color: #94a3b8; }
  }
`;

const CityList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: 768px) {
    max-height: 45vh;
  }
`;

const CityOption = styled.div<{ $active?: boolean }>`
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 500;
  color: ${p => p.$active ? '#1e40af' : '#334155'};
  background: ${p => p.$active ? '#eff6ff' : 'transparent'};
  cursor: pointer;
  transition: background 0.1s;
  display: flex;
  justify-content: space-between;
  align-items: center;
  &:hover { background: ${p => p.$active ? '#eff6ff' : '#f8fafc'}; }

  @media (max-width: 768px) {
    padding: 13px 14px;
    font-size: 15px;
  }
`;

const CityUFTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 6px;
  border-radius: 4px;
`;

// ── Buttons ──
const Btn = styled.button<{ $variant?: 'primary' | 'secondary' | 'ghost' }>`
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: none;

  ${({ $variant }) => {
    if ($variant === 'primary') return `
      background: #2563eb;
      color: white;
      &:hover:not(:disabled) { background: #1d4ed8; transform: translateY(-1px); }
      &:disabled { background: #93c5fd; cursor: not-allowed; }
    `;
    if ($variant === 'ghost') return `
      background: transparent;
      color: #475569;
      border: 1.5px solid #e2e8f0;
      &:hover { background: #f1f5f9; }
    `;
    return `
      background: #f1f5f9;
      color: #475569;
      &:hover { background: #e2e8f0; }
    `;
  }}

  svg { width: 15px; height: 15px; }

  @media (max-width: 480px) { justify-content: center; }
`;

// ── Status message ──
const StatusMsg = styled.div<{ $type: 'info' | 'error' }>`
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  margin-bottom: 14px;
  background: ${p => p.$type === 'error' ? '#fef2f2' : '#eff6ff'};
  color: ${p => p.$type === 'error' ? '#dc2626' : '#1e40af'};
  border: 1px solid ${p => p.$type === 'error' ? '#fecaca' : '#bfdbfe'};
`;

// ── PDF preview ──
const PreviewContainer = styled.div`
  margin-top: 16px;
  border: 1.5px solid #e2e8f0;
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-bottom: 1px solid #e2e8f0;
  background: #f1f5f9;
`;

const PreviewTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  text-transform: uppercase;
  letter-spacing: 0.4px;
`;

const PreviewBody = styled.div`
  height: 480px;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobilePreviewNote = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px;
    font-size: 13px;
    color: #475569;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;

    svg {
      color: #94a3b8;
      flex-shrink: 0;
      width: 18px;
      height: 18px;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  background: #f1f5f9;
  margin: 16px 0;
`;

// ─── Component ────────────────────────────────────────────────────────────────

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const CityReportModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { session, userData } = useAuth();

  // ── Form state ──
  const [selectedCity, setSelectedCity] = useState<CityOption | null>(null);
  const [citySearch, setCitySearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownRect, setDropdownRect] = useState({ top: 0, left: 0, width: 0 });
  const cityBtnRef = React.useRef<HTMLButtonElement>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  // ── Loading / error ──
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Report data ──
  const [reportData, setReportData] = useState<CityReportData | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // ── City options from JSON ──
  const cityOptions: CityOption[] = useMemo(() => {
    return (citiesWithCoordinates as any[]).map(c => ({
      id: c.id,
      nome: c.nome,
      uf: c.microrregiao?.mesorregiao?.UF?.sigla || '',
      population: c.population || 0,
    })).sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, []);

  const filteredCities = useMemo(() => {
    if (!citySearch.trim()) return cityOptions.slice(0, 60);
    const q = citySearch.toLowerCase();
    return cityOptions.filter(
      c => c.nome.toLowerCase().includes(q) || c.uf.toLowerCase().includes(q)
    ).slice(0, 60);
  }, [cityOptions, citySearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handle = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-city-dropdown]')) {
        setDropdownOpen(false);
        setCitySearch('');
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedCity(null);
      setDateFrom('');
      setDateTo(new Date().toISOString().slice(0, 10));
      setReportData(null);
      setShowPreview(false);
      setError(null);
    }
  }, [isOpen]);

  const handleGenerateReport = useCallback(async () => {
    if (!selectedCity || !session?.access_token) return;
    setError(null);
    setLoading(true);
    setReportData(null);
    setShowPreview(false);

    try {
      const token = session.access_token;
      const headers = { Authorization: `Bearer ${token}` };

      // 1. Fetch responsável
      const respRes = await fetch(
        `${API_BASE_URL}/api/v1/cidades/${selectedCity.id}/responsavel`,
        { headers }
      );
      const respJson = respRes.ok ? await respRes.json() : { data: null };
      const responsavelName: string | null = respJson.data?.nome ?? null;

      // 2. Fetch all contacts for group and filter by city client-side
      const contactsRes = await fetch(
        `${API_BASE_URL}/api/v1/eleitores`,
        { headers }
      );
      if (!contactsRes.ok) throw new Error('Erro ao buscar contatos.');
      const contactsJson = await contactsRes.json();
      const allContacts: any[] = contactsJson.data || [];
      const cityContacts = allContacts.filter(c => c.cidade_id === selectedCity.id);
      const multipliers: ReportMultiplier[] = cityContacts
        .filter(c => c.multiplicador === true)
        .map(c => ({
          id: c.id,
          nome: c.nome,
          telefone: c.telefone ?? null,
          email: c.email ?? null,
          profissao: c.profissao ?? null,
          rede_social: c.rede_social ?? null,
          observacoes: c.observacoes ?? null,
        }));

      // 3. Fetch demands for group and filter by city + date range client-side
      const demandsRes = await fetch(
        `${API_BASE_URL}/api/v1/demandas`,
        { headers }
      );
      if (!demandsRes.ok) throw new Error('Erro ao buscar demandas.');
      const demandsJson = await demandsRes.json();
      const allDemands: any[] = demandsJson.data || [];

      const fromDate = dateFrom ? new Date(dateFrom) : null;
      const toDate = dateTo ? new Date(dateTo + 'T23:59:59') : null;

      const cityDemands: ReportDemand[] = allDemands
        .filter(d => {
          if (d.cidade_id !== selectedCity.id) return false;
          // Exclude "Responsável da Cidade" internal demand
          if (d.titulo === 'Responsável da Cidade') return false;
          if (!d.data_cadastro) return true;
          const created = new Date(d.data_cadastro);
          if (fromDate && created < fromDate) return false;
          if (toDate && created > toDate) return false;
          return true;
        })
        .sort(
          (a, b) =>
            new Date(b.data_cadastro).getTime() - new Date(a.data_cadastro).getTime()
        )
        .map(d => ({
          id: d.id,
          titulo: d.titulo,
          tipo: d.tipo,
          status: d.status,
          data_cadastro: d.data_cadastro,
          data_inicio: d.data_inicio ?? null,
          data_fim: d.data_fim ?? null,
          descricao: d.descricao ?? null,
        }));

      // 4. Build date range label
      const fmt = (s: string) =>
        new Date(s).toLocaleDateString('pt-BR');
      let dateRangeLabel = '';
      if (dateFrom && dateTo) {
        dateRangeLabel = `${fmt(dateFrom)} – ${fmt(dateTo)}`;
      } else if (dateFrom) {
        dateRangeLabel = `a partir de ${fmt(dateFrom)}`;
      } else if (dateTo) {
        dateRangeLabel = `até ${fmt(dateTo)}`;
      } else {
        dateRangeLabel = 'todos os períodos';
      }

      // 5. Fetch group name
      let groupName = 'Grupo Político';
      if (userData?.grupo_politico_id) {
        try {
          const groupRes = await fetch(
            `${API_BASE_URL}/api/v1/grupo-politico/${userData.grupo_politico_id}`,
            { headers }
          );
          if (groupRes.ok) {
            const groupJson = await groupRes.json();
            groupName = groupJson.data?.nome ?? groupName;
          }
        } catch {
          // keep default
        }
      }

      setReportData({
        cityName: selectedCity.nome,
        uf: selectedCity.uf,
        population: selectedCity.population,
        contactsCount: cityContacts.length,
        responsavel: responsavelName,
        multipliers,
        demands: cityDemands,
        dateRangeLabel,
        groupName,
        generatedAt: new Date().toLocaleDateString('pt-BR'),
      });

      setShowPreview(true);
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro ao gerar o relatório.');
    } finally {
      setLoading(false);
    }
  }, [selectedCity, dateFrom, dateTo, session, userData]);

  const fileName = useMemo(() => {
    if (!selectedCity) return 'relatorio.pdf';
    const city = selectedCity.nome
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_');
    const date = new Date().toISOString().slice(0, 10);
    return `relatorio_${city}_${date}.pdf`;
  }, [selectedCity]);

  const canGenerate = !!selectedCity && !loading;

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <DragHandle />
        {/* Header */}
        <ModalHeader>
          <ModalIcon>
            <FileText size={20} />
          </ModalIcon>
          <ModalTitleBlock>
            <ModalTitle>Gerar Relatório de Cidade</ModalTitle>
            <ModalSubtitle>Selecione uma cidade e o período de demandas</ModalSubtitle>
          </ModalTitleBlock>
          <CloseBtn onClick={onClose}>
            <X size={18} />
          </CloseBtn>
        </ModalHeader>

        {/* Body */}
        <ModalBody>
          {error && <StatusMsg $type="error">{error}</StatusMsg>}

          {/* City selector */}
          <FormGroup>
            <Label>Município</Label>
            <CityDropdownWrapper data-city-dropdown>
              <CityDropdownButton
                ref={cityBtnRef}
                $hasValue={!!selectedCity}
                onClick={() => {
                  if (!dropdownOpen && cityBtnRef.current) {
                    const r = cityBtnRef.current.getBoundingClientRect();
                    setDropdownRect({ top: r.bottom + 6, left: r.left, width: r.width });
                  }
                  setDropdownOpen(v => !v);
                }}
                type="button"
              >
                <span>
                  {selectedCity
                    ? `${selectedCity.nome} – ${selectedCity.uf}`
                    : 'Selecionar município...'}
                </span>
                <ChevronDown size={15} />
              </CityDropdownButton>

              {dropdownOpen && (
                <CityDropdownMenu $top={dropdownRect.top} $left={dropdownRect.left} $width={dropdownRect.width}>
                  <CitySearchInput>
                    <Search />
                    <input
                      autoFocus
                      placeholder="Buscar município..."
                      value={citySearch}
                      onChange={e => setCitySearch(e.target.value)}
                    />
                  </CitySearchInput>
                  <CityList>
                    {filteredCities.map(c => (
                      <CityOption
                        key={c.id}
                        $active={selectedCity?.id === c.id}
                        onClick={() => {
                          setSelectedCity(c);
                          setDropdownOpen(false);
                          setCitySearch('');
                          setReportData(null);
                          setShowPreview(false);
                        }}
                      >
                        <span>{c.nome}</span>
                        <CityUFTag>{c.uf}</CityUFTag>
                      </CityOption>
                    ))}
                    {filteredCities.length === 0 && (
                      <CityOption as="div" style={{ color: '#94a3b8', fontStyle: 'italic' }}>
                        Nenhum município encontrado
                      </CityOption>
                    )}
                  </CityList>
                </CityDropdownMenu>
              )}
            </CityDropdownWrapper>
          </FormGroup>

          {/* Date range */}
          <FormRow>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>Data Início das Demandas</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={e => {
                  setDateFrom(e.target.value);
                  setReportData(null);
                  setShowPreview(false);
                }}
                max={dateTo || undefined}
              />
            </FormGroup>
            <FormGroup style={{ marginBottom: 0 }}>
              <Label>Data Fim das Demandas</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={e => {
                  setDateTo(e.target.value);
                  setReportData(null);
                  setShowPreview(false);
                }}
                min={dateFrom || undefined}
              />
            </FormGroup>
          </FormRow>

          {/* Preview */}
          {reportData && showPreview && (
            <>
              <Divider />
              <PreviewContainer>
                <PreviewHeader>
                  <PreviewTitle>Pré-visualização</PreviewTitle>
                  <Btn
                    $variant="ghost"
                    style={{ padding: '4px 10px', fontSize: 11 }}
                    onClick={() => setShowPreview(v => !v)}
                  >
                    <EyeOff size={13} />
                    Ocultar
                  </Btn>
                </PreviewHeader>
                <PreviewBody>
                  <PDFViewer width="100%" height="100%" showToolbar={false}>
                    <CityReportDocument data={reportData} />
                  </PDFViewer>
                </PreviewBody>
                <MobilePreviewNote>
                  <Download />
                  Pré-visualização indisponível em dispositivos móveis. Use o botão abaixo para baixar o PDF.
                </MobilePreviewNote>
              </PreviewContainer>
            </>
          )}

          {reportData && !showPreview && (
            <>
              <Divider />
              <StatusMsg $type="info">
                ✓ Relatório gerado com sucesso para <strong>{selectedCity?.nome}</strong>.
                Você pode baixar ou pré-visualizar abaixo.
              </StatusMsg>
            </>
          )}
        </ModalBody>

        {/* Footer */}
        <ModalFooter>
          <Btn $variant="secondary" onClick={onClose}>
            Fechar
          </Btn>

          {reportData && !showPreview && (
            <Btn $variant="ghost" onClick={() => setShowPreview(true)}>
              <Eye size={15} />
              Pré-visualizar
            </Btn>
          )}

          {!reportData ? (
            <Btn
              $variant="primary"
              onClick={handleGenerateReport}
              disabled={!canGenerate}
            >
              {loading ? (
                <>
                  <SpinnerInline /> Gerando...
                </>
              ) : (
                <>
                  <FileText size={15} />
                  Gerar Relatório
                </>
              )}
            </Btn>
          ) : (
            <PDFDownloadLink
              document={<CityReportDocument data={reportData} />}
              fileName={fileName}
              style={{ textDecoration: 'none' }}
            >
              {({ loading: pdfLoading }) => (
                <Btn $variant="primary" as="span">
                  {pdfLoading ? (
                    <>
                      <SpinnerInline /> Preparando...
                    </>
                  ) : (
                    <>
                      <Download size={15} />
                      Baixar PDF
                    </>
                  )}
                </Btn>
              )}
            </PDFDownloadLink>
          )}
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

// ── Inline spinner ──
const spin = keyframes`from { transform: rotate(0deg); } to { transform: rotate(360deg); }`;
const SpinnerRing = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,0.35);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.65s linear infinite;
  flex-shrink: 0;
`;
const SpinnerInline: React.FC = () => <SpinnerRing />;

export default CityReportModal;
